/**
 * Moments in which a hero can speak. Adding one takes three steps: list it here, record `<event name> 1.mp3`, `<event name> 2.mp3`... in a
 * voice pack's folder (the id is the file name with spaces as dashes, so `change target 1.mp3` is `change-target`), and call
 * `heroVoice.say(heroIds, id)` where it happens. Packs without clips for an event stay quiet for it.
 */
import { shadowVoice, type VoiceTreatment } from './voice-spaces.js';

/** Short spells use channel effects only; incantations are reserved for longer casts. */
export const MIN_CASTING_VOICE_SECONDS = 4;

export interface VoiceEventDefinition {
  id: string;
  /** What triggers it, for the settings and the docs. */
  name: string;
  /** Odds (0 to 1) that one trigger is spoken, before the player's voice frequency scales them. */
  chance: number;
  /** Odds when the hero chose this themselves under their behavior settings rather than being ordered. Absent means the same as `chance`. Autonomous actions come far more often than orders, so everyday ones are quieter. */
  autoChance?: number;
  /** Seconds a hero stays silent for this event after speaking it. */
  cooldown: number;
  /** A line starts over one still playing only when its priority is higher; equal and lower lines are dropped. */
  priority: number;
  /** Repeated urgent reactions may replace a line of equal priority. */
  interruptEqual?: boolean;
  /** No pack has clips for it yet (they are listed in `docs/sounds-wanted.md`): it stays quiet until they are recorded. */
  awaitingRecordings?: boolean;
  /** Played lower or in a room of its own, over the speaker's ancestry room (`voice-spaces.ts`). */
  treatment?: VoiceTreatment;
}

/** Everyday commands are chatty by default only now and then; refusals and signature powers are always answered. */
export const voiceEvents: VoiceEventDefinition[] = [
  { id: 'selection', name: 'Hero selected', chance: .3, cooldown: 8, priority: 0 },
  { id: 'move', name: 'Ordered to move', chance: .3, cooldown: 6, priority: 0 },
  { id: 'combat-move', name: 'Ordered to move in a fight', chance: .45, autoChance: .15, cooldown: 5, priority: 0 },
  { id: 'attack', name: 'Ordered to attack', chance: .4, autoChance: .25, cooldown: 5, priority: 0 },
  { id: 'change-target', name: 'Ordered to attack a different foe', chance: .55, autoChance: .2, cooldown: 4, priority: 0 },
  { id: 'stop', name: 'Ordered to stop', chance: .35, cooldown: 6, priority: 0 },
  { id: 'interact', name: 'Interacting with a chest or stall', chance: .6, cooldown: 8, priority: 0 },
  { id: 'loot', name: 'Taking loot', chance: .5, cooldown: 8, priority: 0 },
  { id: 'potion', name: 'Drinking or giving a potion', chance: .7, autoChance: .7, cooldown: 6, priority: 1 },
  { id: 'battlecry', name: 'Critical hit or felling a foe', chance: .35, cooldown: 10, priority: 1 },
  { id: 'pain', name: 'Receiving HP damage', chance: .35, cooldown: 2, priority: 1 },
  { id: 'critical-pain', name: 'Receiving critical HP damage', chance: 1, cooldown: 0, priority: 2.5, interruptEqual: true },
  { id: 'dying', name: 'Entering the dying state', chance: 1, cooldown: 0, priority: 3 },
  { id: 'cooldown', name: 'Power not ready yet', chance: 1, cooldown: 3, priority: 1 },
  { id: 'no-actions', name: 'No action to spend', chance: 1, cooldown: 3, priority: 1 },
  { id: 'coordinated-strike', name: 'Coordinated Strike', chance: 1, cooldown: 0, priority: 2 },
  { id: 'hunter-mark', name: 'Hunter’s Mark', chance: 1, cooldown: 0, priority: 2 },
  // Mockery is the Songweaver's autoattack, cast every few seconds: the jeer is spoken every other cast at most, and never twice within eight seconds.
  { id: 'vicious-mockery', name: 'Vicious Mockery', chance: .5, autoChance: .5, cooldown: 8, priority: 1 },
  // Spoken from the server's ability events, Raging of their own accord or not (`auto-voices.ts`). Rage comes every round, so the war cry rests between.
  { id: 'rage', name: 'Rage (war cry)', chance: 1, cooldown: 12, priority: 2 },
  { id: 'that-all-you-got', name: 'That all you got?!', chance: 1, cooldown: 6, priority: 2 },
  { id: 'lifebinding-spirit', name: 'Lifebinding Spirit', chance: 1, cooldown: 0, priority: 2 },
  { id: 'spirit-mend', name: 'Mend', chance: 1, cooldown: 0, priority: 2 },
  { id: 'true-strike', name: 'True Strike', chance: 1, cooldown: 6, priority: 2 },
  { id: 'entice', name: 'Entice', chance: 1, cooldown: 0, priority: 2 },
  // The Shepherd's autoattack cantrips are spoken now and then, like Vicious Mockery.
  { id: 'rebuke', name: 'Rebuke', chance: .5, autoChance: .5, cooldown: 8, priority: 1 },
  { id: 'withering-touch', name: 'Withering Touch', chance: .5, autoChance: .5, cooldown: 8, priority: 1 },
  // The Shadowmancer speaks from the shadows: every power line is lowered and rings in a dark hall. Shadows are summoned one a turn up to INT.
  { id: 'summon-shadow', name: 'Summon Shadow', chance: 1, cooldown: 8, priority: 2, treatment: shadowVoice },
  { id: 'command-shadows', name: 'Command Shadows', chance: 1, cooldown: 6, priority: 2, treatment: shadowVoice },
  { id: 'shadow-blast', name: 'Shadow Blast', chance: .5, autoChance: .5, cooldown: 8, priority: 1, treatment: shadowVoice },
  { id: 'breath-of-life', name: 'Breath of Life', chance: 1, cooldown: 0, priority: 2 },
  { id: 'songweavers-inspiration', name: 'Songweaver’s Inspiration', chance: 1, cooldown: 6, priority: 2 },
  // Spoken from the server's events (`combatEventVoices`). A blocked blow comes often, so Defend is a grunt now and then.
  { id: 'sneak-attack', name: 'Sneak Attack', chance: .6, cooldown: 6, priority: 1 },
  { id: 'defend', name: 'Defend', chance: .35, cooldown: 8, priority: 1 },
  { id: 'interpose', name: 'Interpose', chance: .8, cooldown: 6, priority: 2 },
  { id: 'assess', name: 'Assess', chance: .7, cooldown: 6, priority: 1 },
  { id: 'anticipate-danger', name: 'Anticipate Danger', chance: .7, cooldown: 8, priority: 1 },
];

/** Powers whose line answers the server's `ability` event for it, for any hero seen, rather than an order or a spent charge. */
export const abilityEventVoices: Record<string, string> = { rage: 'rage', 'that-all-you-got': 'that-all-you-got' };

/** Combat events a hero answers, by event kind, and whether the hero is the event's actor or its target (a Defend is a block on the hero). */
export const combatEventVoices: Readonly<Record<string, { event: string; speaker: 'actor' | 'target' }>> = {
  block: { event: 'defend', speaker: 'target' }, interpose: { event: 'interpose', speaker: 'actor' },
  assess: { event: 'assess', speaker: 'actor' }, anticipate: { event: 'anticipate-danger', speaker: 'actor' },
};

/** The event a class power or tactical action speaks, by ability id. Powers not listed are silent. */
export const abilityVoiceEvents: Record<string, string> = {
  'coordinated-strike': 'coordinated-strike',
  'hunters-mark': 'hunter-mark',
  'vicious-mockery': 'vicious-mockery',
  'lifebinding-spirit': 'lifebinding-spirit',
  'spirit-mend': 'spirit-mend',
  'true-strike': 'true-strike',
  entice: 'entice',
  rebuke: 'rebuke',
  'withering-touch': 'withering-touch',
  'summon-shadow': 'summon-shadow',
  'command-shadows': 'command-shadows',
  'shadow-blast': 'shadow-blast',
  'breath-of-life': 'breath-of-life',
  'songweavers-inspiration': 'songweavers-inspiration',
};
